import { useEffect, useState } from "react"
import { Button, message } from "antd"
import styles from "../worktime.module.scss"
import clsx from "clsx"
import { useRequest } from "ahooks"
import { auditTimeworks, auditTimeworksSz, getWorktimeDailyApi, rejectTimeworksApi } from "@/services/apis/worktimeDaily"
import { useNavigate } from "react-router-dom"
import { useMemo } from "react"
import { add, oneDecimals } from '@/utils'
import { k_pageType } from "../daily/hook"
import { LoadingOutlined } from "@ant-design/icons"
import RejectModal from './RejectModal';
import EstimateWorktimeModal from "../daily/estimateWorktime";
import { addDays, formatDate } from "./util";


interface IProps {
  defaultDate: string
  pageType: string
  onUpdateList: () => void
}

const Index: React.FC<IProps> = (props) => {
  const { defaultDate, pageType, onUpdateList } = props

  const navigate = useNavigate()
  const [isAllWeekday, setIsAllWeekday] = useState(true)
  const [currentDate, setCurrentDate] = useState("")
  
  const {
    data,
    loading: dailyInfoLoading,
    run: runGetWorktimeDailyApi,
    refresh: refreshDailyInfo,
  } = useRequest(async (date: string, type?: string | null, email?: string | null, isAll?: boolean) => {
    const res = await getWorktimeDailyApi(
      date,
      type,
      email,
      isAll
    )
    res.dates = res.dates.map(v => {
      if(type === "sz") {
        return { ...v, status: v.paStatus }
      }
      return v
    })
    return res
  }, {
    manual: true,
  })

  useEffect(() => {
    if (!defaultDate) return
    setIsAllWeekday(true)
    setCurrentDate("")
    runGetWorktimeDailyApi(defaultDate, pageType, "", true)
  }, [defaultDate, pageType])

  const onChangeDate = (date: string, isAll: boolean) => {
    let newDate = date
    if (isAll) {
      newDate = defaultDate
    } else {
      setCurrentDate(date)
    }
    setIsAllWeekday(isAll)
    runGetWorktimeDailyApi(newDate, pageType, "", isAll)
  }

  const currentDateInfo = useMemo(() => {
    if (!data || !data.dates || !currentDate) return null
    return data.dates.find(v => v.spentOn === currentDate)
  }, [data, currentDate])

  const hasAllApprove = useMemo(() => {
    const dates = data?.dates || []
    let hasBtn = false
    dates.forEach(v => {
      let status = v.status
      if (status === 0 || status === 2) {
        hasBtn = true
      }
    })
    return hasBtn
  }, [data])

  const projects = useMemo(() => {
    if (!data || !data.projects) return []
    return data.projects.map(v => {
      let total = 0
      v.users?.forEach((user) => {
        total = add(total, user.total, 2)
      })
      return { ...v, total }
    })
  }, [data])

  const currentData = useMemo(() => {
    let projectsTotal = 0
    if (!data || !data.projects) return {
      userTotal: [],
      projectsTotal: 0,
    }

    const userTotal = data.users?.map((v) => {
      let total = 0
      let biTotal = 0
      data.projects?.map((project) => {
        project.users?.map((user) => {
          if (user.id === v.id) {
            total = add(total, user.total, 2)
            biTotal = add(biTotal, user.biHours, 2)
            projectsTotal = add(projectsTotal, user.total, 2)
          }
        })
      })
      return { total, biTotal, id: v.id }
    })

    return { userTotal, projectsTotal }
  }, [data])
  // auditTimeworks
  const { run: runAuditTimeworks, loading: auditLoading } = useRequest(
    auditTimeworks,
    {
      manual: true,
      onSuccess: () => {
        // 刷新
        onUpdateList()
        refreshDailyInfo()
      },
    }
  )
  // auditTimeworksSz
  const { run: runAuditTimeworksSz, loading: auditLoadingSz } = useRequest(
    auditTimeworksSz,
    {
      manual: true,
      onSuccess: () => {
        // 刷新
        onUpdateList()
        refreshDailyInfo()
      },
    }
  )

  // 审批按钮事件
  const handleAudit = () => {
    let date = currentDate || defaultDate
    if (!date) return
    runAuditTimeworks(date, "", isAllWeekday)
  }

  const handleAuditSz = () => {
    let date = currentDate || defaultDate
    if (!date) return
    runAuditTimeworksSz(date, "", isAllWeekday)
  }

  const {
    run: runRejectTimeworks,
    loading: rejectTimeworksLoading,
  } = useRequest(rejectTimeworksApi, {
    manual: true,
    onSuccess: () => {
      message.success("打回成功")
      onUpdateList()
      refreshDailyInfo()
    },
    onError: (error) => {
      message.error(error.message || "打回失败")
    }
  })

  // Reject Modal State
  const [rejectModal, setRejectModal] = useState<{ isOpen: boolean; user: API.WorktimeDaily.user | null; }>({
      isOpen: false,
      user: null,
  });
  const openRejectModal = (user: API.WorktimeDaily.user) => {
      setRejectModal({ isOpen: true, user });
  };

  const confirmReject = (reason: string) => {
      if (!rejectModal.user || isAllWeekday) return;
      runRejectTimeworks(
          {
              userID: rejectModal.user.id,
              rejectReason: reason,
              date: currentDate,
              type: pageType,
          }
      )
      closeRejectModal()
  };
  const closeRejectModal = () => {
      setRejectModal({ isOpen: false, user: null});
  };

  // Estimate Worktime Modal State
  const [estimateModal, setEstimateModal] = useState<{ isOpen: boolean; user: API.WorktimeDaily.user | null }>({
      isOpen: false,
      user: null,
  });
  const openEstimateModal = (user: API.WorktimeDaily.user) => {
      setEstimateModal({ isOpen: true, user });
  };
  const closeEstimateModal = () => {
      setEstimateModal({ isOpen: false, user: null });
  };

  // 预估工时弹窗查询的时间区间：整周时取周一~周日，单天时取当天
  const dateRange = useMemo(() => {
    if(!data) return { startDate: "", endDate: "" }
    if (isAllWeekday) {
      let startDate: Date, endDate: Date
      const allDates = [...data.dates || []];
      allDates.sort((a, b) => new Date(a.spentOn).getTime() - new Date(b.spentOn).getTime());
      startDate = new Date(allDates?.[0]?.spentOn || "")
      endDate = new Date(allDates?.[allDates?.length - 1]?.spentOn || "")
      return { startDate: formatDate(startDate), endDate: formatDate(endDate) }
    }
    return { startDate: currentDate, endDate: currentDate }
  }, [data, isAllWeekday, currentDate])

  const goEdit = (user: API.WorktimeDaily.user) => {
    if (!user.id) return
    navigate(`/worktime/users/${user.id}/currentDate/${currentDate}?pageType=${pageType}`)
  }
  const approveBtn = () => {
    if (!data) return <></>
    if (isAllWeekday) {
      if (hasAllApprove) {
        return <Button
          type="primary"
          onClick={() => {
            if (pageType === "sz") {
              handleAuditSz()
            } else if (pageType === "zc") {
              handleAudit()
            }
          }}
        >
          全部审批
        </Button>
      }
      return <span className="text-gray-400">已审批</span>
    }
    const current = data.dates?.find((item) => item.spentOn === currentDate)
    if (!current) return <></>
    let status = current.status
    
    switch (status) {
      case 0:
        return (
          <Button
            type="primary"
            onClick={() => {
              if (!currentDate) return
              if (pageType === "sz") {
                handleAuditSz()
              } else if (pageType === "zc") {
                handleAudit()
              }
            }}
          >
            审批
          </Button>
        )
      case 1:
        return <span className="text-gray-400">已审批</span>
      case 2:
        return (
          <Button
            type="primary"
            onClick={() => {
              if (!currentDate) return
              if (pageType === "sz") {
                handleAuditSz()
              } else if (pageType === "zc") {
                handleAudit()
              }
            }}
          >
            更新审批
          </Button>
        )
      default:
        return null
    }
  }
  const listItemDates = () => {
    if (!data) return <></>

    return <div className="flex justify-start gap-3 mt-2 flex-wrap">
      <Button
        type={
          isAllWeekday ? "primary" : "text"
        }
        color="default"
        className={hasAllApprove ? "text-red-500" : ""}
        onClick={() => onChangeDate("", true)}
      >
        全部
      </Button>
      {data.dates?.map((v) => {
        let status = v.status
        return (
          <Button
            disabled={status === -1}
            key={v.spentOn}
            type={
              !isAllWeekday && currentDate === v.spentOn ? "primary" : "text"
            }
            color="default"
            className={status === 0 || status === 2 ? "text-red-500" : ""}
            onClick={() => onChangeDate(v.spentOn, false)}
          >
            {v.spentOn}
            {status === 1 && (
              <span className="text-gray-400 ml-1">
                (已审批)
              </span>
            )}{" "}
            {status === 2 && (
              <span className="text-gray-400 ml-1">
                (更新)
              </span>
            )}
          </Button>
        )
      }
      )}
    </div>
  }
  return (
    <div className="absolute left-0 right-0 top-0 bottom-0 flex flex-col p-2">
      <div className="shrink-0">
        {listItemDates()}
      </div>
      
      <div className="mt-4 bg-white flex-1 overflow-auto p-2" onClick={(e) => e.stopPropagation()}>
        <div>
          <table className={styles.borderTable}>
            <tbody>
              <tr>
                <td>操作</td>
                <td colSpan={3 + (data ? data.users?.length : 0)} className="text-right width-100">
                  <span className="sticky right-2">
                    {approveBtn()}
                  </span>

                </td>
              </tr>
            </tbody>
            <colgroup>
              <col style={{ minWidth: 80 }} />
              <col style={{ minWidth: 100 }} />
              <col style={{ minWidth: 100 }} />
              <col style={{ minWidth: 80 }} />
              {data?.users.map((v) => (
                <col
                  key={v.id}
                  style={{ minWidth: pageType === "zc" ? 80 : 140 }}
                />
              ))}
            </colgroup>
            <tbody>
              <tr>
                <td>分类</td>
                <td>项目编号</td>
                <td>项目名称</td>
                <td>合计</td>
                {data?.users.map((v) => (
                  <td key={v.id}>
                    <button
                      type="button"
                      onClick={() => openEstimateModal(v)}
                      title="查看预估工时"
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                    >
                      {v.name}
                    </button>{" "}
                    {v.majorName &&
                      pageType === "sz" &&
                      `(${v.majorName})`}
                  </td>
                ))}
              </tr>
              {projects.map((v, i) => (
                <tr key={i}>
                  {i === 0 && <td rowSpan={data?.projects.length}>项目</td>}
                  <td>{v.code}</td>
                  <td>{v.name}</td>
                  <td>{v.total}</td>
                  {v.users?.map((user) => (
                    <td
                      key={user.id}
                      className={clsx(
                        "edit-td whitespace-nowrap group hover:bg-gray-50",
                        (user.hours <= 0 || isAllWeekday) && "worktimeEditDisabled"
                      )}
                    >
                      {user.total}
                      {user.biHours > 0 && (
                        <span className="text-red-400">（{user.biHours}）</span>
                      )}
                      {!isAllWeekday && user.total > 0 && currentDateInfo && currentDateInfo.status !== 1 && (
                        <div className="hidden group-hover:flex items-center justify-center gap-2 absolute top-0 left-0 w-full h-full">
                        <Button
                            onClick={() => goEdit(user)}
                            size="small"
                        >修改
                          {/* {pageType === k_pageType.sz ? "查看" : "修改"} */}
                        </Button>
                        <Button
                            onClick={() => openRejectModal(user)}
                            size="small"
                        >打回
                          {/* {pageType === k_pageType.sz ? "查看" : "修改"} */}
                          </Button>
                          </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td colSpan={3}>项目合计</td>
                <td>{currentData.projectsTotal}</td>
                {currentData?.userTotal?.map((v) => (
                  <td key={v.id} className="font-bold whitespace-nowrap">
                    {v.total}
                    {v.biTotal > 0 && (
                      <span className="text-red-400">（{v.biTotal}）</span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <RejectModal
        isOpen={rejectModal.isOpen}
        onClose={closeRejectModal}
        onConfirm={confirmReject}
        userName={rejectModal.user?.name || ""}
        period={currentDate}
      />
      <EstimateWorktimeModal
        open={estimateModal.isOpen}
        user={estimateModal.user}
        type={pageType}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onClose={closeEstimateModal}
      />
    </div>
  )
}

export default Index
